import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Separator } from '../ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  ChevronLeft, Plus, Trash2, Save, Zap, ArrowRight, ArrowDown,
  Settings, X, Mail, MessageSquare, GitBranch, Bot, Layers,
  Clock, Play, FileText, UserPlus, Tag, Bell, CheckSquare, Pause
} from 'lucide-react';
import { cn } from '../../lib/utils';

// Category options matching PM requirements
const CATEGORIES = [
  { id: 'email', label: 'Email Automation', icon: Mail, description: 'Automated email sequences and responses' },
  { id: 'sms', label: 'SMS Automation', icon: MessageSquare, description: 'Text message workflows' },
  { id: 'status_flow', label: 'Status Flow', icon: GitBranch, description: 'Automated status transitions' },
  { id: 'ai', label: 'AI Automation', icon: Bot, description: 'AI-powered decision making' },
  { id: 'platform', label: 'Platform Automation', icon: Layers, description: 'System-wide automation' },
];

// Trigger types
const TRIGGER_TYPES = [
  { id: 'lead_created', label: 'Lead Created', icon: UserPlus, category: 'entity' },
  { id: 'status_changed', label: 'Status Changed', icon: GitBranch, category: 'entity' },
  { id: 'job_stage_changed', label: 'Job Stage Changed', icon: ArrowRight, category: 'entity' },
  { id: 'appointment_scheduled', label: 'Appointment Scheduled', icon: Clock, category: 'calendar' },
  { id: 'time_based', label: 'Time-based / Scheduled', icon: Clock, category: 'time' },
  { id: 'manual', label: 'Manual Trigger', icon: Play, category: 'manual' },
];

// Action types
const ACTION_TYPES = [
  { id: 'send_email', label: 'Send Email', icon: Mail, category: 'communication', safeToDryRun: false },
  { id: 'send_sms', label: 'Send SMS', icon: MessageSquare, category: 'communication', safeToDryRun: false },
  { id: 'update_status', label: 'Update Status', icon: GitBranch, category: 'internal', safeToDryRun: true },
  { id: 'create_task', label: 'Create Task', icon: CheckSquare, category: 'internal', safeToDryRun: true },
  { id: 'send_notification', label: 'Send Notification', icon: Bell, category: 'internal', safeToDryRun: true },
  { id: 'add_tag', label: 'Add Tag', icon: Tag, category: 'internal', safeToDryRun: true },
  { id: 'delay', label: 'Wait / Delay', icon: Clock, category: 'flow', safeToDryRun: true },
  { id: 'trigger_automation', label: 'Trigger Another Automation', icon: Zap, category: 'flow', safeToDryRun: true },
];

// Condition operators
const OPERATORS = [
  { id: 'equals', label: 'Equals' },
  { id: 'not_equals', label: 'Does not equal' },
  { id: 'contains', label: 'Contains' },
  { id: 'not_contains', label: 'Does not contain' },
  { id: 'is_empty', label: 'Is empty' },
  { id: 'is_not_empty', label: 'Is not empty' },
  { id: 'greater_than', label: 'Greater than' },
  { id: 'less_than', label: 'Less than' },
];

// Condition fields
const CONDITION_FIELDS = [
  { id: 'lead.status', label: 'Lead Status' },
  { id: 'lead.source', label: 'Lead Source' },
  { id: 'job.stage', label: 'Job Stage' },
  { id: 'job.type', label: 'Job Type' },
  { id: 'job.value', label: 'Job Value' },
  { id: 'contact.city', label: 'Contact City' },
  { id: 'contact.state', label: 'Contact State' },
];

export const AutomationBuilder = ({ automation, onSave, onCancel }) => {
  const [form, setForm] = useState({
    name: automation?.name || '',
    description: automation?.description || '',
    category: automation?.category || 'platform',
    status: automation?.status || 'draft',
    triggers: automation?.triggers || [],
    condition_groups: automation?.condition_groups || [],
    actions: automation?.actions || [],
  });

  const [activeStep, setActiveStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const steps = [
    { id: 'basics', label: 'Basics', description: 'Name and category' },
    { id: 'trigger', label: 'Trigger', description: 'When to run' },
    { id: 'conditions', label: 'Conditions', description: 'Optional filters' },
    { id: 'actions', label: 'Actions', description: 'What to do' },
  ];

  // Validation
  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 0) {
      if (!form.name.trim()) newErrors.name = 'Name is required';
      if (!form.category) newErrors.category = 'Category is required';
    }
    if (step === 1) {
      if (form.triggers.length === 0) newErrors.triggers = 'At least one trigger is required';
    }
    if (step === 3) {
      if (form.actions.length === 0) newErrors.actions = 'At least one action is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goToNextStep = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const goToPrevStep = () => {
    setActiveStep(prev => Math.max(prev - 1, 0));
  };

  // Add trigger
  const addTrigger = (type) => {
    const triggerConfig = TRIGGER_TYPES.find(t => t.id === type);
    const newTrigger = {
      id: `trigger-${Date.now()}`,
      type,
      config: {},
    };
    setForm(prev => ({
      ...prev,
      triggers: [...prev.triggers, newTrigger],
    }));
  };

  // Remove trigger
  const removeTrigger = (triggerId) => {
    setForm(prev => ({
      ...prev,
      triggers: prev.triggers.filter(t => t.id !== triggerId),
    }));
  };

  // Update trigger
  const updateTrigger = (triggerId, updates) => {
    setForm(prev => ({
      ...prev,
      triggers: prev.triggers.map(t =>
        t.id === triggerId ? { ...t, ...updates } : t
      ),
    }));
  };

  // Add action
  const addAction = (type) => {
    const actionConfig = ACTION_TYPES.find(a => a.id === type);
    const newAction = {
      id: `action-${Date.now()}`,
      type,
      order: form.actions.length,
      config: {},
    };
    setForm(prev => ({
      ...prev,
      actions: [...prev.actions, newAction],
    }));
  };

  // Remove action
  const removeAction = (actionId) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.filter(a => a.id !== actionId).map((a, idx) => ({ ...a, order: idx })),
    }));
  };

  // Update action
  const updateAction = (actionId, updates) => {
    setForm(prev => ({
      ...prev,
      actions: prev.actions.map(a =>
        a.id === actionId ? { ...a, ...updates } : a
      ),
    }));
  };

  // Add condition group
  const addConditionGroup = () => {
    const newGroup = {
      id: `group-${Date.now()}`,
      logic: 'and',
      conditions: [{
        id: `cond-${Date.now()}`,
        field: '',
        operator: 'equals',
        value: '',
      }],
    };
    setForm(prev => ({
      ...prev,
      condition_groups: [...prev.condition_groups, newGroup],
    }));
  };

  // Remove condition group
  const removeConditionGroup = (groupId) => {
    setForm(prev => ({
      ...prev,
      condition_groups: prev.condition_groups.filter(g => g.id !== groupId),
    }));
  };

  // Add condition to group
  const addConditionToGroup = (groupId) => {
    setForm(prev => ({
      ...prev,
      condition_groups: prev.condition_groups.map(g =>
        g.id === groupId
          ? {
              ...g,
              conditions: [...g.conditions, {
                id: `cond-${Date.now()}`,
                field: '',
                operator: 'equals',
                value: '',
              }],
            }
          : g
      ),
    }));
  };

  // Update condition
  const updateCondition = (groupId, conditionId, updates) => {
    setForm(prev => ({
      ...prev,
      condition_groups: prev.condition_groups.map(g =>
        g.id === groupId
          ? {
              ...g,
              conditions: g.conditions.map(c =>
                c.id === conditionId ? { ...c, ...updates } : c
              ),
            }
          : g
      ),
    }));
  };

  // Remove condition
  const removeCondition = (groupId, conditionId) => {
    setForm(prev => ({
      ...prev,
      condition_groups: prev.condition_groups.map(g =>
        g.id === groupId
          ? { ...g, conditions: g.conditions.filter(c => c.id !== conditionId) }
          : g
      ),
    }));
  };

  // Handle save
  const handleSave = async () => {
    // Validate all steps
    for (let i = 0; i < steps.length; i++) {
      if (!validateStep(i)) {
        setActiveStep(i);
        return;
      }
    }

    setIsSaving(true);
    try {
      await onSave(form);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10 bg-card/50">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onCancel} data-testid="builder-back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold">
              {automation?.id ? 'Edit Automation' : 'Create Automation'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {form.name || 'Untitled automation'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={form.status}
            onValueChange={(val) => setForm(prev => ({ ...prev, status: val }))}
          >
            <SelectTrigger className="w-[130px] bg-card/50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button 
            onClick={handleSave} 
            disabled={isSaving}
            className="shadow-[0_0_15px_rgba(59,130,246,0.3)]"
            data-testid="save-automation-btn"
          >
            {isSaving ? 'Saving...' : 'Save Automation'}
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Steps */}
        <div className="w-64 border-r border-white/10 bg-card/30 p-4">
          <div className="space-y-2">
            {steps.map((step, idx) => (
              <button
                key={step.id}
                onClick={() => setActiveStep(idx)}
                className={cn(
                  "w-full text-left p-3 rounded-lg transition-all",
                  activeStep === idx
                    ? "bg-primary/10 border-l-4 border-primary"
                    : "hover:bg-white/5 border-l-4 border-transparent"
                )}
                data-testid={`step-${step.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium",
                    activeStep === idx ? "bg-primary text-white" : "bg-white/10 text-muted-foreground",
                    activeStep > idx && "bg-emerald-500 text-white"
                  )}>
                    {activeStep > idx ? '✓' : idx + 1}
                  </div>
                  <div>
                    <div className={cn(
                      "font-medium text-sm",
                      activeStep === idx ? "text-primary" : "text-foreground"
                    )}>
                      {step.label}
                    </div>
                    <div className="text-xs text-muted-foreground">{step.description}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Visual Flow Preview */}
          <div className="mt-8 pt-4 border-t border-white/10">
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
              Flow Preview
            </p>
            <div className="space-y-2">
              {form.triggers.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-blue-400" />
                  <span className="text-muted-foreground">
                    {form.triggers.length} trigger{form.triggers.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
              {form.triggers.length > 0 && (
                <div className="flex justify-center">
                  <ArrowDown className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
              {form.condition_groups.length > 0 && (
                <>
                  <div className="flex items-center gap-2 text-xs">
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <span className="text-muted-foreground">
                      {form.condition_groups.length} condition group{form.condition_groups.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex justify-center">
                    <ArrowDown className="h-4 w-4 text-muted-foreground" />
                  </div>
                </>
              )}
              {form.actions.length > 0 && (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-2 h-2 rounded-full bg-emerald-400" />
                  <span className="text-muted-foreground">
                    {form.actions.length} action{form.actions.length > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6 max-w-3xl mx-auto">
            {/* Step 0: Basics */}
            {activeStep === 0 && (
              <div className="space-y-6" data-testid="step-basics-content">
                <div>
                  <h2 className="text-2xl font-semibold mb-1">Basic Information</h2>
                  <p className="text-muted-foreground">Give your automation a name and select its type</p>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Automation Name *</Label>
                    <Input
                      id="name"
                      value={form.name}
                      onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Welcome Email Sequence"
                      className={cn("mt-1 bg-card/50", errors.name && "border-red-500")}
                      data-testid="automation-name-input"
                    />
                    {errors.name && <p className="text-xs text-red-400 mt-1">{errors.name}</p>}
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={form.description}
                      onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="What does this automation do?"
                      className="mt-1 bg-card/50"
                      rows={3}
                      data-testid="automation-description-input"
                    />
                  </div>

                  <div>
                    <Label>Category *</Label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
                      {CATEGORIES.map(cat => {
                        const Icon = cat.icon;
                        return (
                          <Card
                            key={cat.id}
                            className={cn(
                              "cursor-pointer transition-all hover:border-primary/50",
                              form.category === cat.id
                                ? "border-primary bg-primary/5"
                                : "border-white/10 bg-card/50"
                            )}
                            onClick={() => setForm(prev => ({ ...prev, category: cat.id }))}
                            data-testid={`category-${cat.id}`}
                          >
                            <CardContent className="p-4 flex items-start gap-3">
                              <div className={cn(
                                "p-2 rounded-lg",
                                form.category === cat.id ? "bg-primary/20" : "bg-white/5"
                              )}>
                                <Icon className={cn(
                                  "h-5 w-5",
                                  form.category === cat.id ? "text-primary" : "text-muted-foreground"
                                )} />
                              </div>
                              <div>
                                <div className="font-medium">{cat.label}</div>
                                <div className="text-xs text-muted-foreground">{cat.description}</div>
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                    {errors.category && <p className="text-xs text-red-400 mt-1">{errors.category}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* Step 1: Triggers */}
            {activeStep === 1 && (
              <div className="space-y-6" data-testid="step-trigger-content">
                <div>
                  <h2 className="text-2xl font-semibold mb-1">Trigger</h2>
                  <p className="text-muted-foreground">Define when this automation should run</p>
                </div>

                {form.triggers.length === 0 ? (
                  <Card className="border-dashed border-white/20 bg-card/30">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Zap className="h-10 w-10 text-muted-foreground mb-3" />
                      <p className="font-medium mb-1">No trigger selected</p>
                      <p className="text-sm text-muted-foreground mb-4">Choose what starts this automation</p>
                      {errors.triggers && <p className="text-xs text-red-400 mb-4">{errors.triggers}</p>}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {form.triggers.map(trigger => {
                      const config = TRIGGER_TYPES.find(t => t.id === trigger.type);
                      const Icon = config?.icon || Zap;
                      return (
                        <Card key={trigger.id} className="border-white/10 bg-card/50">
                          <CardHeader className="pb-3">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 bg-blue-500/10 rounded-lg">
                                  <Icon className="h-5 w-5 text-blue-400" />
                                </div>
                                <div>
                                  <CardTitle className="text-base">{config?.label || trigger.type}</CardTitle>
                                </div>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-red-400"
                                onClick={() => removeTrigger(trigger.id)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {trigger.type === 'status_changed' && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">From Status</Label>
                                  <Input
                                    value={trigger.config?.from_status || ''}
                                    onChange={(e) => updateTrigger(trigger.id, {
                                      config: { ...trigger.config, from_status: e.target.value }
                                    })}
                                    placeholder="Any"
                                    className="mt-1 bg-background/50"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs">To Status</Label>
                                  <Input
                                    value={trigger.config?.to_status || ''}
                                    onChange={(e) => updateTrigger(trigger.id, {
                                      config: { ...trigger.config, to_status: e.target.value }
                                    })}
                                    placeholder="Any"
                                    className="mt-1 bg-background/50"
                                  />
                                </div>
                              </div>
                            )}
                            {trigger.type === 'time_based' && (
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs">Schedule Type</Label>
                                  <Select
                                    value={trigger.config?.schedule_type || 'daily'}
                                    onValueChange={(val) => updateTrigger(trigger.id, {
                                      config: { ...trigger.config, schedule_type: val }
                                    })}
                                  >
                                    <SelectTrigger className="mt-1 bg-background/50">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="daily">Daily</SelectItem>
                                      <SelectItem value="weekly">Weekly</SelectItem>
                                      <SelectItem value="monthly">Monthly</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">Time</Label>
                                  <Input
                                    type="time"
                                    value={trigger.config?.time || '09:00'}
                                    onChange={(e) => updateTrigger(trigger.id, {
                                      config: { ...trigger.config, time: e.target.value }
                                    })}
                                    className="mt-1 bg-background/50"
                                  />
                                </div>
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Add Trigger
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                    {TRIGGER_TYPES.map(trigger => {
                      const Icon = trigger.icon;
                      const isSelected = form.triggers.some(t => t.type === trigger.id);
                      return (
                        <Button
                          key={trigger.id}
                          variant="outline"
                          className={cn(
                            "justify-start h-auto py-3 border-white/10",
                            isSelected && "opacity-50 cursor-not-allowed"
                          )}
                          onClick={() => !isSelected && addTrigger(trigger.id)}
                          disabled={isSelected}
                          data-testid={`add-trigger-${trigger.id}`}
                        >
                          <Icon className="h-4 w-4 mr-2 text-blue-400" />
                          {trigger.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Conditions */}
            {activeStep === 2 && (
              <div className="space-y-6" data-testid="step-conditions-content">
                <div>
                  <h2 className="text-2xl font-semibold mb-1">Conditions</h2>
                  <p className="text-muted-foreground">Add optional filters to control when actions run</p>
                </div>

                {form.condition_groups.length === 0 ? (
                  <Card className="border-dashed border-white/20 bg-card/30">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <Settings className="h-10 w-10 text-muted-foreground mb-3" />
                      <p className="font-medium mb-1">No conditions set</p>
                      <p className="text-sm text-muted-foreground mb-4">
                        Automation will run for all matching triggers
                      </p>
                      <Button variant="outline" onClick={addConditionGroup} data-testid="add-condition-group-btn">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Condition Group
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {form.condition_groups.map((group, groupIdx) => (
                      <Card key={group.id} className="border-white/10 bg-card/50">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-sm">Condition Group {groupIdx + 1}</CardTitle>
                            <div className="flex items-center gap-2">
                              <Select
                                value={group.logic}
                                onValueChange={(val) => setForm(prev => ({
                                  ...prev,
                                  condition_groups: prev.condition_groups.map(g =>
                                    g.id === group.id ? { ...g, logic: val } : g
                                  )
                                }))}
                              >
                                <SelectTrigger className="w-24 h-8 bg-background/50">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="and">AND</SelectItem>
                                  <SelectItem value="or">OR</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-red-400"
                                onClick={() => removeConditionGroup(group.id)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          {group.conditions.map((condition, condIdx) => (
                            <div key={condition.id} className="flex items-center gap-2">
                              <Select
                                value={condition.field}
                                onValueChange={(val) => updateCondition(group.id, condition.id, { field: val })}
                              >
                                <SelectTrigger className="w-[180px] bg-background/50">
                                  <SelectValue placeholder="Select field" />
                                </SelectTrigger>
                                <SelectContent>
                                  {CONDITION_FIELDS.map(field => (
                                    <SelectItem key={field.id} value={field.id}>
                                      {field.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Select
                                value={condition.operator}
                                onValueChange={(val) => updateCondition(group.id, condition.id, { operator: val })}
                              >
                                <SelectTrigger className="w-[150px] bg-background/50">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {OPERATORS.map(op => (
                                    <SelectItem key={op.id} value={op.id}>
                                      {op.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {!['is_empty', 'is_not_empty'].includes(condition.operator) && (
                                <Input
                                  value={condition.value}
                                  onChange={(e) => updateCondition(group.id, condition.id, { value: e.target.value })}
                                  placeholder="Value"
                                  className="flex-1 bg-background/50"
                                />
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-red-400"
                                onClick={() => removeCondition(group.id, condition.id)}
                                disabled={group.conditions.length === 1}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          ))}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addConditionToGroup(group.id)}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Add Condition
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                    <Button variant="outline" onClick={addConditionGroup}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add Another Condition Group
                    </Button>
                  </div>
                )}
              </div>
            )}

            {/* Step 3: Actions */}
            {activeStep === 3 && (
              <div className="space-y-6" data-testid="step-actions-content">
                <div>
                  <h2 className="text-2xl font-semibold mb-1">Actions</h2>
                  <p className="text-muted-foreground">Define what happens when this automation runs</p>
                </div>

                {form.actions.length === 0 ? (
                  <Card className="border-dashed border-white/20 bg-card/30">
                    <CardContent className="flex flex-col items-center justify-center py-12">
                      <ArrowRight className="h-10 w-10 text-muted-foreground mb-3" />
                      <p className="font-medium mb-1">No actions defined</p>
                      <p className="text-sm text-muted-foreground mb-4">Add actions to make your automation do something</p>
                      {errors.actions && <p className="text-xs text-red-400 mb-4">{errors.actions}</p>}
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {form.actions.map((action, idx) => {
                      const config = ACTION_TYPES.find(a => a.id === action.type);
                      const Icon = config?.icon || Zap;
                      return (
                        <div key={action.id} className="relative">
                          {idx > 0 && (
                            <div className="absolute -top-2 left-6 w-0.5 h-4 bg-white/10" />
                          )}
                          <Card className="border-white/10 bg-card/50">
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <Badge variant="secondary" className="text-xs">
                                    #{idx + 1}
                                  </Badge>
                                  <div className="p-2 bg-emerald-500/10 rounded-lg">
                                    <Icon className="h-5 w-5 text-emerald-400" />
                                  </div>
                                  <div>
                                    <CardTitle className="text-base">{config?.label || action.type}</CardTitle>
                                    {!config?.safeToDryRun && (
                                      <Badge variant="outline" className="text-xs text-amber-400 border-amber-400/30 mt-1">
                                        Dry-run only
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-muted-foreground hover:text-red-400"
                                  onClick={() => removeAction(action.id)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                              {/* Action-specific config */}
                              {['send_email', 'send_sms'].includes(action.type) && (
                                <div>
                                  <Label className="text-xs">Message Template</Label>
                                  <Textarea
                                    value={action.config?.message || ''}
                                    onChange={(e) => updateAction(action.id, {
                                      config: { ...action.config, message: e.target.value }
                                    })}
                                    placeholder="Hi {{contact.first_name}}, ..."
                                    className="mt-1 bg-background/50"
                                    rows={3}
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Use {'{{variable}}'} syntax for dynamic content
                                  </p>
                                </div>
                              )}
                              {action.type === 'update_status' && (
                                <div>
                                  <Label className="text-xs">New Status</Label>
                                  <Input
                                    value={action.config?.new_status || ''}
                                    onChange={(e) => updateAction(action.id, {
                                      config: { ...action.config, new_status: e.target.value }
                                    })}
                                    placeholder="qualified"
                                    className="mt-1 bg-background/50"
                                  />
                                </div>
                              )}
                              {action.type === 'create_task' && (
                                <>
                                  <div>
                                    <Label className="text-xs">Task Title</Label>
                                    <Input
                                      value={action.config?.task_title || ''}
                                      onChange={(e) => updateAction(action.id, {
                                        config: { ...action.config, task_title: e.target.value }
                                      })}
                                      placeholder="Follow up with customer"
                                      className="mt-1 bg-background/50"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Due in (days)</Label>
                                    <Input
                                      type="number"
                                      value={action.config?.due_days || ''}
                                      onChange={(e) => updateAction(action.id, {
                                        config: { ...action.config, due_days: parseInt(e.target.value) || 0 }
                                      })}
                                      placeholder="3"
                                      className="mt-1 w-24 bg-background/50"
                                    />
                                  </div>
                                </>
                              )}
                              {action.type === 'delay' && (
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <Label className="text-xs">Days</Label>
                                    <Input
                                      type="number"
                                      value={action.config?.days || ''}
                                      onChange={(e) => updateAction(action.id, {
                                        config: { ...action.config, days: parseInt(e.target.value) || 0 }
                                      })}
                                      className="mt-1 bg-background/50"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Hours</Label>
                                    <Input
                                      type="number"
                                      value={action.config?.hours || ''}
                                      onChange={(e) => updateAction(action.id, {
                                        config: { ...action.config, hours: parseInt(e.target.value) || 0 }
                                      })}
                                      className="mt-1 bg-background/50"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs">Minutes</Label>
                                    <Input
                                      type="number"
                                      value={action.config?.minutes || ''}
                                      onChange={(e) => updateAction(action.id, {
                                        config: { ...action.config, minutes: parseInt(e.target.value) || 0 }
                                      })}
                                      className="mt-1 bg-background/50"
                                    />
                                  </div>
                                </div>
                              )}
                              {action.type === 'add_tag' && (
                                <div>
                                  <Label className="text-xs">Tag Name</Label>
                                  <Input
                                    value={action.config?.tag || ''}
                                    onChange={(e) => updateAction(action.id, {
                                      config: { ...action.config, tag: e.target.value }
                                    })}
                                    placeholder="hot-lead"
                                    className="mt-1 bg-background/50"
                                  />
                                </div>
                              )}
                              {action.type === 'send_notification' && (
                                <div>
                                  <Label className="text-xs">Notification Message</Label>
                                  <Input
                                    value={action.config?.message || ''}
                                    onChange={(e) => updateAction(action.id, {
                                      config: { ...action.config, message: e.target.value }
                                    })}
                                    placeholder="New lead requires attention"
                                    className="mt-1 bg-background/50"
                                  />
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div>
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">
                    Add Action
                  </Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {ACTION_TYPES.map(action => {
                      const Icon = action.icon;
                      return (
                        <Button
                          key={action.id}
                          variant="outline"
                          className="justify-start h-auto py-3 border-white/10"
                          onClick={() => addAction(action.id)}
                          data-testid={`add-action-${action.id}`}
                        >
                          <Icon className="h-4 w-4 mr-2 text-emerald-400" />
                          {action.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-8 border-t border-white/10 mt-8">
              <Button
                variant="outline"
                onClick={goToPrevStep}
                disabled={activeStep === 0}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Previous
              </Button>
              {activeStep < steps.length - 1 ? (
                <Button onClick={goToNextStep} data-testid="next-step-btn">
                  Next Step
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSave}
                  disabled={isSaving}
                  className="shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                  data-testid="final-save-btn"
                >
                  {isSaving ? 'Saving...' : 'Save Automation'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationBuilder;
